/**
 * ClassifierTab Component
 *
 * Tab for skills classification configuration.
 * Response classifier toggle and quick instructions are now in Prompts tab.
 */

import React from 'react';
import { SkillsTab } from './SkillsTab';
import { getConfigValue, ValidationResult } from '@/config/fieldConfig';
import type { AgentConfiguration } from '@/types';

interface ClassifierTabProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
  appContext: any;
  agentType: string;
  /** Section to highlight after navigation */
  highlightSection?: string | null;
  errors?: Record<string, ValidationResult>;
}

export const ClassifierTab: React.FC<ClassifierTabProps> = ({
  config,
  onChange,
  agentType,
}) => {
  // Skills configuration values
  const skills = getConfigValue(config, 'hooks.classifier.skills') || [];
  const skillsEnabled = getConfigValue(config, 'hooks.classifier.skills_enabled') ?? false;
  const semanticMatchingEnabled = getConfigValue(config, 'hooks.classifier.semantic_matching_enabled') ?? false;
  const semanticSimilarityThreshold = getConfigValue(config, 'hooks.classifier.semantic_similarity_threshold') ?? 0.75;
  const classifierEnabled = getConfigValue(config, 'hooks.classifier.enabled') ?? false;
  const classifierVisible = getConfigValue(config, 'hooks_visible.classifier') !== false;
  const ruleConfidenceThreshold = getConfigValue(config, 'hooks.classifier.rule_confidence_threshold') ?? 0.8;
  const skillLlmTimeout = getConfigValue(config, 'hooks.classifier.skill_llm_timeout') ?? 5.0;
  const skillToolInstructions = getConfigValue(config, 'hooks.classifier.skill_tool_instructions') ?? '';

  // Classification rules
  const llmFallbackEnabled = getConfigValue(config, 'hooks.classifier.llm_fallback_enabled') ?? true;
  const customQuickKeywords: string[] = getConfigValue(config, 'hooks.classifier.custom_quick_keywords') ?? [];
  const customQuickPatterns: string[] = getConfigValue(config, 'hooks.classifier.custom_quick_patterns') ?? [];

  // Skill dedup configuration
  const dedupConfig = {
    enabled: getConfigValue(config, 'hooks.skill_dedup.enabled') ?? false,
    data_query_hours: getConfigValue(config, 'hooks.skill_dedup.data_query_hours'),
    report_window_hours: getConfigValue(config, 'hooks.skill_dedup.report_window_hours'),
    max_per_window: getConfigValue(config, 'hooks.skill_dedup.max_per_window'),
    output_preview_length: getConfigValue(config, 'hooks.skill_dedup.output_preview_length'),
    show_user_query: getConfigValue(config, 'hooks.skill_dedup.show_user_query'),
    query_preview_length: getConfigValue(config, 'hooks.skill_dedup.query_preview_length'),
  };

  // Skill tracking enabled (required for dedup)
  const skillTrackingEnabled = getConfigValue(config, 'hooks.skill_tracking.enabled') ?? false;

  // Handler for dedup config changes
  const handleDedupConfigChange = (field: string, value: boolean | number | null) => {
    onChange(`hooks.skill_dedup.${field}`, value);
  };

  return (
    <SkillsTab
      skills={skills}
      onSkillsChange={(newSkills) => onChange('hooks.classifier.skills', newSkills)}
      skillsEnabled={skillsEnabled}
      onSkillsEnabledChange={(enabled) => onChange('hooks.classifier.skills_enabled', enabled)}
      semanticMatchingEnabled={semanticMatchingEnabled}
      onSemanticMatchingEnabledChange={(enabled) => onChange('hooks.classifier.semantic_matching_enabled', enabled)}
      semanticSimilarityThreshold={semanticSimilarityThreshold}
      onSemanticSimilarityThresholdChange={(threshold) => onChange('hooks.classifier.semantic_similarity_threshold', threshold)}
      ruleConfidenceThreshold={ruleConfidenceThreshold}
      onRuleConfidenceThresholdChange={(threshold) => onChange('hooks.classifier.rule_confidence_threshold', threshold)}
      skillLlmTimeout={skillLlmTimeout}
      onSkillLlmTimeoutChange={(timeout) => onChange('hooks.classifier.skill_llm_timeout', timeout)}
      skillToolInstructions={skillToolInstructions}
      onSkillToolInstructionsChange={(instructions) => onChange('hooks.classifier.skill_tool_instructions', instructions)}
      classifierEnabled={classifierEnabled}
      classifierVisible={classifierVisible}
      dedupConfig={dedupConfig}
      onDedupConfigChange={handleDedupConfigChange}
      skillTrackingEnabled={skillTrackingEnabled}
      agentContext={`Agent: ${config.name || agentType}\nDescription: ${config.description || ''}`}
      llmFallbackEnabled={llmFallbackEnabled}
      onLlmFallbackEnabledChange={(enabled) => onChange('hooks.classifier.llm_fallback_enabled', enabled)}
      customQuickKeywords={customQuickKeywords}
      onCustomQuickKeywordsChange={(keywords) => onChange('hooks.classifier.custom_quick_keywords', keywords)}
      customQuickPatterns={customQuickPatterns}
      onCustomQuickPatternsChange={(patterns) => onChange('hooks.classifier.custom_quick_patterns', patterns)}
    />
  );
};

export default ClassifierTab;
